import type { ReactNode } from "react";

/**
 * Generic responsive grid for stat tiles.
 *
 * The layout primitive that backs every dashboard stat row. Unlike
 * {@link DashboardStatGrid} (which is coupled to `DashboardStat[]` and a fixed
 * 4-up ramp), this takes arbitrary children — typically `<StatCard>`s — and a
 * target column count, so the ~15 surfaces that hand-roll `grid-cols-N` around
 * StatCards can share one ramp instead of forking the breakpoints each time.
 *
 * `cols` is the column count at the widest breakpoint; the ramp always starts
 * at 1 column and steps through 2 before reaching `cols`, so 3-up and 5-up
 * layouts are preserved (pass `cols={3}` / `cols={5}`) rather than rounded to
 * the old fixed 1/2/4.
 *
 * Column classes are spelled out statically below so Tailwind's scanner keeps
 * them — do not build them by interpolation.
 */
export type StatGridCols = 2 | 3 | 4 | 5;

const COL_RAMP: Record<StatGridCols, string> = {
  2: "grid-cols-1 @sm/page:grid-cols-2",
  3: "grid-cols-1 @sm/page:grid-cols-2 @lg/page:grid-cols-3",
  4: "grid-cols-1 @sm/page:grid-cols-2 @lg/page:grid-cols-4",
  5: "grid-cols-1 @sm/page:grid-cols-2 @lg/page:grid-cols-5",
};

export function StatGrid({
  cols = 4,
  // Bare metrics (no tile) need horizontal air to read as distinct numbers,
  // not a cramped row — minimal design language (docs/design/MINIMAL-SURFACES-PRD.md).
  gap = "gap-x-8 gap-y-6",
  className,
  children,
}: {
  /** Column count at the widest breakpoint. Ramps 1 → 2 → cols. */
  cols?: StatGridCols;
  /** Gap utility; override only when a surface intentionally tightens it. */
  gap?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={["grid", gap, COL_RAMP[cols], className]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </div>
  );
}
